/* eslint-disable */
import React, { useState, useCallback, useRef } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { LOCALSTORAGE_KEY } from "../../framework/constant/constant";
import { utility } from "../../framework/utility/utilityProvider";
import { useTranslation } from "../../locale/useTranslation";

// Material UI imports
import {
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Collapse,
    Box,
    Tooltip,
    Popover,
    Paper,
    Typography,
} from "@mui/material";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import CircleIcon from "@mui/icons-material/Circle";

// Local imports
import { sidebarTheme, getIconColor, scrollbarStyles } from "./SidebarStyles";
import getMuiIcon from "./menuIcons";

const Menu = (props) => {
    const location = useLocation();
    const lang = useTranslation();
    const { SID } = useParams();
    const { menuModel, isMinified } = props;

    // Track which parent menu is expanded (accordion - only one at a time)
    const [expandedMenu, setExpandedMenu] = useState(null);
    // Track nested submenu expansion
    const [expandedSubMenu, setExpandedSubMenu] = useState({});
    // Track hover popover for minified state
    const [hoverAnchor, setHoverAnchor] = useState(null);
    const [hoverMenu, setHoverMenu] = useState(null);
    const closeTimeoutRef = useRef(null);
    const isOverPopoverRef = useRef(false);
    const isOverTriggerRef = useRef(false);

    // Build flat list of menus for finding current active item
    const buildMenuList = useCallback(() => {
        let menus = [];
        menuModel.forEach((x) => {
            if (x.type === 1) {
                let allSubMenu = [];
                x.subMenu?.forEach((y) => {
                    if (y.type === 1) {
                        allSubMenu = [...allSubMenu, ...y.subMenu];
                    } else {
                        allSubMenu = [...allSubMenu, y];
                    }
                });
                menus = [...menus, ...x.subMenu, ...allSubMenu];
            } else {
                menus = [...menus, x];
            }
        });
        return menus;
    }, [menuModel]);

    const menus = buildMenuList();
    const pathname = location.pathname?.split(`/${SID}`);
    const currentMenu = menus.find(
        (x) => pathname[0]?.replace("/home/", "") === x.link
    ) ?? { name: "Dashboard", link: null, modulesid: null };

    // Handle parent menu click (accordion behavior)
    const handleMenuClick = (menuName) => {
        setExpandedMenu((prev) => (prev === menuName ? null : menuName));
    };

    // Handle nested submenu click
    const handleSubMenuClick = (subMenuName) => {
        setExpandedSubMenu((prev) => ({
            ...prev,
            [subMenuName]: !prev[subMenuName],
        }));
    };

    // Handle external link
    const handleExternalLink = (path, e) => {
        e.preventDefault();
        e.stopPropagation();
        window.open(path, "_blank");
    };

    // Close popover with delay check
    const tryClosePopover = () => {
        if (closeTimeoutRef.current) {
            clearTimeout(closeTimeoutRef.current);
        }
        closeTimeoutRef.current = setTimeout(() => {
            if (!isOverPopoverRef.current && !isOverTriggerRef.current) {
                setHoverAnchor(null);
                setHoverMenu(null);
            }
        }, 100);
    };

    // Handle hover for minified popover
    const handleMouseEnter = (event, item) => {
        if (isMinified && item.type === 1) {
            if (closeTimeoutRef.current) {
                clearTimeout(closeTimeoutRef.current);
            }
            isOverTriggerRef.current = true;
            setHoverAnchor(event.currentTarget);
            setHoverMenu(item);
        }
    };

    const handleMouseLeave = () => {
        if (isMinified) {
            isOverTriggerRef.current = false;
            tryClosePopover();
        }
    };

    const handlePopoverEnter = () => {
        isOverPopoverRef.current = true;
        if (closeTimeoutRef.current) {
            clearTimeout(closeTimeoutRef.current);
        }
    };

    const handlePopoverLeave = () => {
        isOverPopoverRef.current = false;
        tryClosePopover();
    };

    // Get user-permitted menus
    const userMenu = utility.getUserMenu(menuModel);

    // Base item styles
    const listItemStyles = {
        py: 0.5,
        px: 1.5,
        minHeight: 36,
        transition: sidebarTheme.transition,
        "&:hover": {
            backgroundColor: sidebarTheme.hoverBg,
        },
    };

    // Active item - light background, dark text (matching original)
    const activeItemStyles = {
        backgroundColor: sidebarTheme.activeBg,
        "&:hover": {
            backgroundColor: sidebarTheme.activeBg,
        },
    };

    // Default text style - same size for all states
    const textStyles = {
        "& .MuiTypography-root": {
            fontSize: "0.8125rem",
            fontWeight: 400,
            lineHeight: 1.4,
            color: sidebarTheme.textColor,
        },
    };

    // Active text - just color change, no size or weight change
    const activeTextStyles = {
        "& .MuiTypography-root": {
            fontSize: "0.8125rem",
            fontWeight: 400,
            lineHeight: 1.4,
            color: sidebarTheme.activeText,
        },
    };

    // Popover text styles (same as sidebar - light text on dark background)
    const popoverTextStyles = {
        "& .MuiTypography-root": {
            fontSize: "0.8125rem",
            fontWeight: 400,
            lineHeight: 1.4,
            color: sidebarTheme.textColor,
        },
    };

    const popoverActiveTextStyles = {
        "& .MuiTypography-root": {
            fontSize: "0.8125rem",
            fontWeight: 400,
            lineHeight: 1.4,
            color: sidebarTheme.activeText,
        },
    };

    const betaBadgeStyles = {
        fontWeight: 700,
        textTransform: "uppercase",
        fontSize: "0.55rem",
        letterSpacing: "0.35px",
        color: "#b0bec5",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "rgba(255,255,255,0.06)",
        px: 0.4,
        py: 0.1,
        borderRadius: 1,
        border: "1px solid rgba(255,255,255,0.12)",
        ml: 0.4,
    };

    // Submenu container with left border line
    const submenuContainerStyles = {
        borderLeft: `1px solid ${sidebarTheme.submenuBorderColor}`,
        ml: 3,
        pl: 0,
    };

    // Render a leaf menu item (type === 0)
    const renderLeafItem = (item, isNested = false, isDeepNested = false, inPopover = false) => {
        const isActive = currentMenu.link === item.link;
        const iconColor = getIconColor(item.name);
        const showExternalLink =
            item.name === lang.scheduling_menu || item.name === lang.planning_sub_menu;
        const showBetaBadge = item.showBetaBadge;

        // For submenu items, use the item's icon or a default folder icon
        const getItemIcon = () => {
            // Use actual icon from menu model
            return getMuiIcon(item.icon, isActive ? sidebarTheme.activeText : iconColor, isNested ? 16 : 18);
        };

        const itemStyles = inPopover ? {
            py: 0.5,
            px: 1.5,
            minHeight: 32,
            "&:hover": {
                backgroundColor: sidebarTheme.hoverBg,
            },
            ...(isActive ? activeItemStyles : {}),
        } : {
            ...listItemStyles,
            ...(isActive ? activeItemStyles : {}),
            pl: isDeepNested ? 1.5 : isNested ? 1.5 : 1.5,
        };

        return (
            <ListItemButton
                key={item.name + item.link}
                component={Link}
                to={item.link}
                sx={itemStyles}
            >
                <ListItemIcon sx={{ minWidth: 28 }}>
                    {getItemIcon()}
                </ListItemIcon>
                {(!isMinified || inPopover) && (
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flex: 1, minWidth: 0 }}>
                        <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, minWidth: 0 }}>
                            <Typography
                                variant="body2"
                                noWrap
                                sx={inPopover ? (isActive ? popoverActiveTextStyles["& .MuiTypography-root"] : popoverTextStyles["& .MuiTypography-root"]) : (isActive ? activeTextStyles["& .MuiTypography-root"] : textStyles["& .MuiTypography-root"])}
                            >
                                {item.name}
                            </Typography>
                            {showBetaBadge && (
                                <Typography variant="caption" sx={betaBadgeStyles}>
                                    BETA
                                </Typography>
                            )}
                        </Box>
                        {showExternalLink && (
                            <OpenInNewIcon
                                onClick={(e) =>
                                    handleExternalLink(
                                        `/home/${item.name === lang.scheduling_menu ? "Scheduling" : "planning"}`,
                                        e
                                    )
                                }
                                sx={{
                                    fontSize: 14,
                                    color: isActive ? sidebarTheme.activeText : sidebarTheme.textColorMuted,
                                    "&:hover": { color: isActive ? sidebarTheme.activeText : "#fff" },
                                }}
                            />
                        )}
                    </Box>
                )}
            </ListItemButton>
        );
    };

    // Render submenu items for popover
    const renderPopoverSubMenu = (subMenu) => {
        return subMenu?.map((subItem) => {
            if (subItem.type === 0) {
                return renderLeafItem(subItem, false, false, true);
            } else if (subItem.type === 1) {
                // Nested parent in popover - show as expandable (simplified)
                return (
                    <Box key={subItem.name + "nested"}>
                        <ListItemButton
                            sx={{
                                py: 0.5,
                                px: 1.5,
                                minHeight: 32,
                                "&:hover": { backgroundColor: sidebarTheme.hoverBg },
                            }}
                            onClick={() => handleSubMenuClick(subItem.name)}
                        >
                            <ListItemIcon sx={{ minWidth: 28 }}>
                                {getMuiIcon(subItem.icon, getIconColor(subItem.name), 16)}
                            </ListItemIcon>
                            <ListItemText primary={subItem.name} sx={popoverTextStyles} />
                            {expandedSubMenu[subItem.name] ? (
                                <ExpandLess sx={{ fontSize: 16, color: sidebarTheme.textColorMuted }} />
                            ) : (
                                <ExpandMore sx={{ fontSize: 16, color: sidebarTheme.textColorMuted }} />
                            )}
                        </ListItemButton>
                        <Collapse in={expandedSubMenu[subItem.name]} timeout={150}>
                            <Box sx={{ pl: 2 }}>
                                {subItem.subMenu?.map((nestedItem) =>
                                    renderLeafItem(nestedItem, false, false, true)
                                )}
                            </Box>
                        </Collapse>
                    </Box>
                );
            }
            return null;
        });
    };

    // Render a parent menu item with submenu (type === 1)
    const renderParentItem = (item, isNested = false) => {
        const isExpanded = isNested
            ? expandedSubMenu[item.name]
            : expandedMenu === item.name;
        const iconColor = getIconColor(item.name);

        return (
            <Box key={item.name + "parent"}>
                <Tooltip title={isMinified && !hoverMenu ? item.name : ""} placement="right" arrow>
                    <ListItemButton
                        onClick={() => !isMinified && (isNested ? handleSubMenuClick(item.name) : handleMenuClick(item.name))}
                        onMouseEnter={(e) => handleMouseEnter(e, item)}
                        onMouseLeave={handleMouseLeave}
                        sx={{
                            ...listItemStyles,
                            pl: isNested ? 1.5 : 1.5,
                        }}
                    >
                        <ListItemIcon sx={{ minWidth: 28 }}>
                            {getMuiIcon(item.icon, iconColor, 18)}
                        </ListItemIcon>
                        {!isMinified && (
                            <>
                                <ListItemText
                                    primary={item.name}
                                    sx={textStyles}
                                    disableTypography={false}
                                />
                                {isExpanded ? (
                                    <ExpandLess sx={{ color: sidebarTheme.textColorMuted, fontSize: 18 }} />
                                ) : (
                                    <ExpandMore sx={{ color: sidebarTheme.textColorMuted, fontSize: 18 }} />
                                )}
                            </>
                        )}
                    </ListItemButton>
                </Tooltip>

                {!isMinified && (
                    <Collapse in={isExpanded} timeout={150} unmountOnExit>
                        <Box sx={submenuContainerStyles}>
                            <List component="div" disablePadding>
                                {item.subMenu?.map((subItem) => {
                                    if (subItem.type === 0) {
                                        return renderLeafItem(subItem, true, isNested);
                                    } else if (subItem.type === 1) {
                                        return renderParentItem(subItem, true);
                                    }
                                    return null;
                                })}
                            </List>
                        </Box>
                    </Collapse>
                )}
            </Box>
        );
    };

    return (
        <>
            <List
                component="nav"
                dense
                sx={{
                    width: "100%",
                    py: 0.5,
                }}
            >
                {userMenu.map((item) => {
                    if (item.type === 0) {
                        return (
                            <Tooltip
                                key={item.name + item.link}
                                title={isMinified ? item.name : ""}
                                placement="right"
                                arrow
                            >
                                <span>{renderLeafItem(item)}</span>
                            </Tooltip>
                        );
                    } else if (item.type === 1) {
                        return renderParentItem(item);
                    }
                    return null;
                })}
            </List>

            {/* Flyout menu for minified state */}
            {isMinified && hoverMenu && (
                <Box
                    onMouseEnter={handlePopoverEnter}
                    onMouseLeave={handlePopoverLeave}
                    sx={{
                        position: "fixed",
                        left: sidebarTheme.drawerWidthMinified,
                        top: hoverAnchor?.getBoundingClientRect()?.top || 100,
                        zIndex: 1300,
                        minWidth: 220,
                        maxHeight: 400,
                        overflowY: "auto",
                        backgroundColor: sidebarTheme.backgroundColor,
                        boxShadow: "0 4px 20px rgba(0,0,0,0.3)",
                        borderRadius: 1,
                        ...scrollbarStyles,
                    }}
                >
                    {/* Header */}
                    <Box sx={{
                        px: 1.5,
                        py: 1,
                        borderBottom: `1px solid ${sidebarTheme.submenuBorderColor}`,
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                    }}>
                        <ListItemIcon sx={{ minWidth: 28 }}>
                            {getMuiIcon(hoverMenu.icon, getIconColor(hoverMenu.name), 18)}
                        </ListItemIcon>
                        <ListItemText
                            primary={hoverMenu.name}
                            sx={{
                                "& .MuiTypography-root": {
                                    fontSize: "0.8125rem",
                                    fontWeight: 500,
                                    color: sidebarTheme.textColor,
                                }
                            }}
                        />
                    </Box>
                    {/* Submenu items */}
                    <List component="div" disablePadding dense>
                        {renderPopoverSubMenu(hoverMenu.subMenu)}
                    </List>
                </Box>
            )}
        </>
    );
};

export default Menu;